import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ticketsAPI, knowledgeAPI } from '@/lib/api';
import { formatRelativeTime, priorityColors, statusColors } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Headphones,
  Plus,
  Search,
  TicketIcon,
  BookOpen,
  MessageSquare,
  Clock,
  LogOut,
  ChevronRight,
  Loader2,
  FileText
} from 'lucide-react';
import NewTicketDialog from '@/components/NewTicketDialog';
import CustomerTicketView from '@/components/CustomerTicketView';
import AIChat from '@/components/AIChat';
import WorkOrderView from '@/components/WorkOrderView';

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [activeTab, setActiveTab] = useState('tickets');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Debug logging for ticket visibility
      console.log('[CustomerPortal] Loading tickets for user:', user);
      
      const [ticketsRes, articlesRes] = await Promise.all([
        ticketsAPI.list({}),
        knowledgeAPI.list({ published_only: true })
      ]);
      
      console.log('[CustomerPortal] Tickets received:', ticketsRes.data.length);
      console.log('[CustomerPortal] User ID:', user?.id);
      console.log('[CustomerPortal] User role:', user?.role);
      
      // Verify ticket ownership
      ticketsRes.data.forEach((ticket, index) => {
        if (ticket.customer_id !== user?.id) {
          console.warn(`[CustomerPortal] ⚠️ TICKET VISIBILITY ISSUE: Ticket ${ticket.ticket_number} belongs to customer ${ticket.customer_id}, but current user is ${user?.id}`);
        }
      });
      
      setTickets(ticketsRes.data);
      setArticles(articlesRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredArticles = articles.filter(a =>
    searchQuery === '' ||
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (selectedTicket) {
    return (
      <CustomerTicketView 
        ticket={selectedTicket} 
        onBack={() => {
          setSelectedTicket(null);
          loadData();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-portal">
      {/* Header */}
      <header className="border-b border-slate-200 bg-slate-50 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Headphones className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-800">Support Portal</span>
          </Link>

          <div className="flex items-center gap-4">
            <Button 
              className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20"
              onClick={() => setShowNewTicket(true)}
              data-testid="create-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>

            <div className="flex items-center gap-3">
              <Avatar className="w-9 h-9 border-2 border-slate-200">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-cyan-100 text-cyan-600">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-slate-700">{user?.name}</span>
              <Button 
                variant="ghost" 
                size="icon"
                className="text-slate-400 hover:text-slate-600"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2 text-slate-800">Hi {user?.name?.split(' ')[0]}, how can we help?</h1>
          <p className="text-slate-500">Search our knowledge base or submit a support ticket</p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search for help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-14 bg-white border-slate-200 text-lg rounded-xl shadow-sm"
            data-testid="search-input"
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 shadow-sm">
            <TabsTrigger value="tickets" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white" data-testid="tickets-tab">
              <TicketIcon className="w-4 h-4 mr-2" />
              My Tickets ({tickets.length})
            </TabsTrigger>
            <TabsTrigger value="work-orders" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white" data-testid="work-orders-tab">
              <FileText className="w-4 h-4 mr-2" />
              Work Orders
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white" data-testid="knowledge-tab">
              <BookOpen className="w-4 h-4 mr-2" />
              Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="chat" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white" data-testid="chat-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              AI Assistant
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tickets">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : tickets.length === 0 ? (
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="py-12 text-center">
                  <TicketIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium mb-2 text-slate-700">No tickets yet</h3>
                  <p className="text-slate-500 mb-4">Create your first support ticket to get help</p>
                  <Button 
                    className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
                    onClick={() => setShowNewTicket(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Ticket
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <Card 
                    key={ticket.id}
                    className="bg-white border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setSelectedTicket(ticket)}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm text-slate-500 font-mono">{ticket.ticket_number}</span>
                            <Badge className={`${statusColors[ticket.status]}`}>
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                            <Badge className={`${priorityColors[ticket.priority]}`}>
                              {ticket.priority}
                            </Badge>
                          </div>
                          <h3 className="font-medium mb-1 text-slate-800">{ticket.subject}</h3>
                          <p className="text-sm text-slate-500 line-clamp-1">{ticket.description}</p>
                        </div>
                        <div className="flex items-center gap-2 text-slate-400">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">{formatRelativeTime(ticket.created_at)}</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="work-orders">
            <WorkOrderView customerId={user?.id} />
          </TabsContent>

          <TabsContent value="knowledge">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : filteredArticles.length === 0 ? (
              <Card className="bg-white border-slate-200 shadow-sm">
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium mb-2 text-slate-700">No articles found</h3>
                  <p className="text-slate-500">Try a different search term</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredArticles.map((article) => (
                  <Link key={article.id} to={`/knowledge/${article.id}`}>
                    <Card className="bg-white border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all h-full">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-cyan-600" />
                          </div>
                          <div>
                            <Badge variant="outline" className="mb-2 text-xs border-slate-200 text-slate-600">
                              {article.category}
                            </Badge>
                            <h3 className="font-medium mb-1 text-slate-800">{article.title}</h3>
                            <p className="text-sm text-slate-500 line-clamp-2">
                              {article.content.substring(0, 150)}...
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="chat">
            <AIChat />
          </TabsContent>
        </Tabs>
      </main>

      <NewTicketDialog 
        open={showNewTicket} 
        onOpenChange={setShowNewTicket}
        currentUser={user}
        onCreated={(ticket) => {
          loadData();
          setSelectedTicket(ticket);
        }}
      />
    </div>
  );
}
